import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Phone, MessageCircle, Mail, MapPin, Clock, Zap, Send, Paperclip, X,
  ClipboardList, ArrowRight, Info, CheckCircle2, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { TopoBackground } from "@/components/TopoBackground";
import { MobileNav } from "@/components/MobileNav";
import { useSiteImages } from "@/lib/siteImages";
import { CONTACT_METHODS, ACCEPTED_TYPES } from "@/lib/constants";
import { createInquiry, uploadFile, getSiteContact } from "@/lib/api";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/83fd665c-12e1-46ec-b34b-7c468a7eac31/images/e7112c3fb1b86f7fc7ed5cb290f5734a0e6d3f95a3f95a2dac81c7e66b08b962.jpeg";

const inputCls =
  "h-11 rounded-md border-[#d2dad5] bg-white text-[#1a221e] placeholder:text-[#9aa5a0] focus-visible:ring-2 focus-visible:ring-[#c8a452] focus-visible:ring-offset-0";

const Brand = () => (
  <Link to="/" className="flex items-center gap-3">
    <img src="/data-earth-logo.webp" alt="Data Earth Surveying" className="h-10 w-10 object-contain" />
    <span className="font-serif leading-none text-[#163323]">
      <span className="block text-lg font-bold tracking-[0.12em]">DATA EARTH</span>
      <span className="block text-[11px] font-medium tracking-[0.42em]">SURVEYING</span>
    </span>
  </Link>
);

const FAQ = [
  {
    q: "How do I choose the correct surveying service?",
    a: "Not sure which survey you need? Describe your project in the message field or reach out directly, and a representative will recommend the right service — construction surveying, topographical, boundary, as-built, volume calculations and more.",
  },
  {
    q: "Does submitting a request confirm an appointment?",
    a: "No. Sending a message or a survey request does not confirm a booking. A representative will contact you to confirm the scope, timing and pricing before any surveying work is scheduled.",
  },
  {
    q: "Can I upload plans and property documents?",
    a: "Yes. You can attach plans, drawings, deeds or site photos (PDF, DOC, JPG or PNG) using the upload field on this page, or share them later once we are in touch.",
  },
  {
    q: "Who does Data Earth Surveying serve?",
    a: "We work with homeowners, developers, contractors, architects, engineers and project teams across The Bahamas — from single lots to large multi-phase developments.",
  },
];

const CONTACT_ROWS = [
  { key: "phone", Icon: Phone, label: "Phone Number", placeholder: "Add Business Number" },
  { key: "whatsapp", Icon: MessageCircle, label: "WhatsApp", placeholder: "Add WhatsApp Number" },
  { key: "email", Icon: Mail, label: "Email", placeholder: "Add Business Email" },
  { key: "service_area", Icon: MapPin, label: "Service Area", placeholder: "Add Service Area" },
  { key: "office_hours", Icon: Clock, label: "Office Hours", placeholder: "Add Office Hours" },
  { key: "response_time", Icon: Zap, label: "Response Time", placeholder: "Add Expected Response Time" },
];

export default function Contact() {
  const navigate = useNavigate();
  const { img } = useSiteImages();
  const [contact, setContact] = useState({});
  const [form, setForm] = useState({
    full_name: "", company_name: "", phone: "", email: "",
    preferred_contact_method: "Call", subject: "", message: "",
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    getSiteContact().then(setContact).catch(() => setContact({}));
  }, []);

  const nav = [
    ["Home", "/"], ["Our Services", "/services"], ["About Us", "/about"],
    ["Projects", "/projects"], ["Request a Survey", "/request"], ["Contact", "/contact"],
  ];

  const telHref = contact.phone ? `tel:${contact.phone.replace(/[^\d+]/g, "")}` : null;
  const waHref = contact.whatsapp ? `https://wa.me/${contact.whatsapp.replace(/[^\d]/g, "")}` : null;
  const mailHref = contact.email ? `mailto:${contact.email}` : null;

  const submit = async () => {
    if (!form.full_name.trim()) return toast.error("Full name is required");
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return toast.error("A valid email address is required");
    if (!form.subject.trim()) return toast.error("Please add a subject");
    if (!form.message.trim()) return toast.error("Please write a message");
    setSubmitting(true);
    try {
      let documents = [];
      if (file) {
        const res = await uploadFile(file);
        documents = [{
          id: res.id, storage_path: res.storage_path, original_filename: res.original_filename,
          content_type: res.content_type, size: res.size,
        }];
      }
      const res = await createInquiry({ ...form, documents });
      setConfirmation(res);
      setForm({
        full_name: "", company_name: "", phone: "", email: "",
        preferred_contact_method: "Call", subject: "", message: "",
      });
      setFile(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const quickActions = useMemo(() => ([
    { label: "Call Us", Icon: Phone, href: telHref, testid: "quick-call", solid: true },
    { label: "Message on WhatsApp", Icon: MessageCircle, href: waHref, testid: "quick-whatsapp", solid: false },
    { label: "Send an Email", Icon: Mail, href: mailHref, testid: "quick-email", solid: false },
  ]), [telHref, waHref, mailHref]);

  return (
    <div className="min-h-screen bg-[#f4f5f4]" data-testid="contact-page">
      {/* HEADER */}
      <header className="sticky top-0 z-50 border-b border-[#e6eae7] bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <Brand />
          <nav className="hidden items-center gap-7 lg:flex">
            {nav.map(([label, href]) => {
              const active = label === "Contact";
              const cls = `relative text-sm font-medium transition-colors ${active ? "text-[#163323]" : "text-[#5a6660] hover:text-[#163323]"}`;
              return (
                <Link key={label} to={href} data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`} className={cls}>
                  {label}
                  {active && <span className="absolute -bottom-1.5 left-0 h-0.5 w-full rounded bg-[#c8a452]" />}
                </Link>
              );
            })}
          </nav>
          <div className="hidden items-center gap-3 text-right xl:flex">
            <div className="font-serif text-[10px] uppercase leading-tight tracking-[0.2em] text-[#5a6660]">
              Surveying Today<br /><span className="text-[#c8a452]">A Stronger Tomorrow</span>
            </div>
          </div>
          <MobileNav buttonClassName="rounded-full bg-[#163323] p-2 text-white lg:hidden" />
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0d2116]">
        <img src={img("contact_hero")} alt="Data Earth surveyor with total station on a Bahamian coastline" className="absolute inset-0 h-full w-full object-cover object-right" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d2116] via-[#163323]/90 to-transparent" />
        <TopoBackground className="opacity-100" opacity={0.07} />
        <div className="relative mx-auto max-w-[1400px] px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.3em] text-[#c8a452]">Contact Us</span>
            <h1 className="mt-3 font-serif text-4xl font-bold leading-[1.05] text-white sm:text-5xl">
              Let&rsquo;s Talk About Your Project
            </h1>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-white/80">
              Have a question about our surveying services or need help deciding what your project
              requires? Contact Data Earth Surveying, and a representative will respond as soon as possible.
            </p>
          </motion.div>
        </div>
      </section>

      {/* MAIN */}
      <div className="mx-auto grid max-w-[1400px] gap-6 px-4 py-10 sm:px-6 lg:grid-cols-3 lg:px-8">
        {/* FORM */}
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="rounded-xl border border-[#e6eae7] bg-white p-6 shadow-sm sm:p-8 lg:col-span-2"
        >
          <h2 className="font-serif text-2xl font-bold text-[#163323]">Send Us a Message</h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-[#1a221e]">Full Name <span className="text-[#c0392b]">*</span></Label>
              <Input data-testid="contact-full-name" className={inputCls} placeholder="Enter your full name"
                value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-[#1a221e]">Company Name (Optional)</Label>
              <Input data-testid="contact-company" className={inputCls} placeholder="Enter company name"
                value={form.company_name} onChange={(e) => set("company_name", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-[#1a221e]">Phone Number</Label>
              <Input data-testid="contact-phone" className={inputCls} placeholder="Enter your phone number"
                value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-[#1a221e]">Email Address <span className="text-[#c0392b]">*</span></Label>
              <Input data-testid="contact-email" type="email" className={inputCls} placeholder="Enter your email address"
                value={form.email} onChange={(e) => set("email", e.target.value)} />
            </div>
          </div>

          <div className="mt-5 grid gap-5 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-[#1a221e]">Preferred Contact Method</Label>
              <RadioGroup data-testid="contact-method" value={form.preferred_contact_method}
                onValueChange={(v) => set("preferred_contact_method", v)} className="flex flex-wrap gap-4 pt-2">
                {CONTACT_METHODS.map((m) => (
                  <label key={m} className="flex cursor-pointer items-center gap-2 text-sm text-[#1a221e]">
                    <RadioGroupItem value={m} className="border-[#163323] text-[#163323]" /> {m}
                  </label>
                ))}
              </RadioGroup>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-[#1a221e]">Subject <span className="text-[#c0392b]">*</span></Label>
              <Input data-testid="contact-subject" className={inputCls} placeholder="What is this about?"
                value={form.subject} onChange={(e) => set("subject", e.target.value)} />
            </div>
          </div>

          <div className="mt-5 space-y-1.5">
            <Label className="text-sm font-medium text-[#1a221e]">Message <span className="text-[#c0392b]">*</span></Label>
            <Textarea data-testid="contact-message" rows={5}
              className="rounded-md border-[#d2dad5] bg-white placeholder:text-[#9aa5a0] focus-visible:ring-2 focus-visible:ring-[#c8a452] focus-visible:ring-offset-0"
              placeholder="Tell us how we can help…"
              value={form.message} onChange={(e) => set("message", e.target.value)} />
          </div>

          <div className="mt-5 space-y-1.5">
            <Label className="text-sm font-medium text-[#1a221e]">Upload a Document or Photo (Optional)</Label>
            <label data-testid="contact-file-label"
              className="flex cursor-pointer items-center gap-3 rounded-md border border-[#d2dad5] bg-white p-2.5 text-sm text-[#5a6660] transition-colors hover:border-[#c8a452]">
              <span className="inline-flex items-center gap-2 rounded bg-[#f6f1e3] px-3 py-1.5 text-xs font-semibold text-[#8e6e23]">
                <Paperclip className="h-3.5 w-3.5" /> Choose File
              </span>
              <span className="truncate">{file ? file.name : "No file chosen"}</span>
              {file && (
                <button type="button" data-testid="contact-remove-file"
                  onClick={(e) => { e.preventDefault(); setFile(null); }}
                  className="ml-auto text-[#9aa5a0] hover:text-[#c0392b]"><X className="h-4 w-4" /></button>
              )}
              <input data-testid="contact-file-input" type="file" accept={ACCEPTED_TYPES} className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
          </div>

          <Button data-testid="contact-submit-btn" onClick={submit} disabled={submitting}
            className="mt-6 h-12 w-full rounded-md bg-[#163323] text-base font-semibold text-white hover:bg-[#0d2116] disabled:opacity-60">
            {submitting ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Sending…</> : <><Send className="mr-2 h-5 w-5" /> Send Message</>}
          </Button>

          <p className="mt-4 text-center text-xs text-[#9aa5a0]">
            Sending a message does not schedule surveying work. To submit complete project details,
            please use the <Link to="/request" className="font-semibold text-[#b38f3f] hover:underline">Request a Survey</Link> form.
          </p>
        </motion.div>

        {/* SIDEBAR */}
        <motion.aside
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
          className="space-y-6"
        >
          <div className="rounded-xl border border-[#e6eae7] bg-white p-6 shadow-sm">
            <h3 className="font-serif text-xl font-bold text-[#163323]">Contact Information</h3>
            <div className="mt-4 divide-y divide-[#f0f2f0]">
              {CONTACT_ROWS.map(({ key, Icon, label, placeholder }) => {
                const value = contact[key];
                return (
                  <div key={key} data-testid={`contact-info-${key}`} className="flex items-center gap-3 py-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#f6f1e3] text-[#b38f3f]">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="w-24 shrink-0 text-sm font-semibold text-[#163323]">{label}</span>
                    <span className={`text-sm ${value ? "text-[#1a221e]" : "italic text-[#9aa5a0]"}`}>
                      {value || placeholder}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 flex items-start gap-2 rounded-lg bg-[#eef0ee] p-3">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#5a6660]" />
              <p className="text-xs text-[#5a6660]">Contact details will be activated after verification.</p>
            </div>
          </div>

          <div className="rounded-xl border border-[#e6eae7] bg-white p-6 shadow-sm">
            <h3 className="font-serif text-xl font-bold text-[#163323]">Quick Actions</h3>
            <div className="mt-4 space-y-3">
              {quickActions.map(({ label, Icon, href, testid, solid }) => (
                href ? (
                  <a key={testid} data-testid={testid} href={href}
                    target={testid === "quick-whatsapp" ? "_blank" : undefined} rel="noopener noreferrer"
                    className={`flex items-center gap-3 rounded-lg border px-4 py-3 text-sm font-semibold transition-all ${
                      solid ? "border-[#163323] bg-[#163323] text-white hover:bg-[#0d2116]"
                            : "border-[#d2dad5] bg-white text-[#163323] hover:border-[#c8a452] hover:bg-[#f6f1e3]"
                    }`}>
                    <Icon className="h-4 w-4" /> {label}
                  </a>
                ) : (
                  <span key={testid} data-testid={`${testid}-disabled`}
                    className="flex cursor-not-allowed items-center gap-3 rounded-lg border border-dashed border-[#d2dad5] bg-[#f8f9f8] px-4 py-3 text-sm font-semibold text-[#b8c0bb]"
                    title="Available after contact details are added">
                    <Icon className="h-4 w-4" /> {label}
                  </span>
                )
              ))}
            </div>
          </div>
        </motion.aside>
      </div>

      {/* REQUEST CTA */}
      <section className="border-y border-[#e5d5ab] bg-[#f6f1e3]">
        <div className="mx-auto flex max-w-[1400px] flex-col items-start gap-5 px-4 py-8 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
          <div className="flex items-start gap-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#163323] text-[#c8a452]">
              <ClipboardList className="h-5 w-5" />
            </span>
            <div>
              <h3 className="font-serif text-xl font-bold text-[#163323]">Ready to Request Surveying Services?</h3>
              <p className="mt-1 max-w-2xl text-sm text-[#5a6660]">
                Provide your project location, service requirements, preferred date, and available plans for professional review.
              </p>
            </div>
          </div>
          <Button data-testid="contact-request-btn" onClick={() => navigate("/request")}
            className="shrink-0 rounded-full bg-[#c8a452] px-6 font-semibold text-[#163323] hover:bg-[#b38f3f]">
            Request a Survey <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-[1400px] px-4 py-12 sm:px-6 lg:px-8">
        <h2 className="font-serif text-2xl font-bold text-[#163323]">Frequently Asked Questions</h2>
        <Accordion type="single" collapsible className="mt-6 grid gap-3 lg:grid-cols-2">
          {FAQ.map((item, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-${i}`}
              className="rounded-lg border border-[#e6eae7] bg-white px-4">
              <AccordionTrigger className="text-left text-sm font-semibold text-[#163323] hover:no-underline">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-[#5a6660]">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* FOOTER */}
      <footer className="relative overflow-hidden bg-[#0d2116] text-white">
        <TopoBackground opacity={0.06} />
        <div className="relative mx-auto flex max-w-[1400px] flex-col items-center gap-4 px-4 py-6 sm:px-6 lg:flex-row lg:justify-between lg:px-8">
          <span className="font-serif text-sm italic text-white/80">Accurate. Reliable. Built for Your Project.</span>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-white/70">
            <Link to="/" className="transition-colors hover:text-[#c8a452]">Home</Link>
            <Link to="/services" className="transition-colors hover:text-[#c8a452]">Our Services</Link>
            <Link to="/about" className="transition-colors hover:text-[#c8a452]">About Us</Link>
            <Link to="/projects" className="transition-colors hover:text-[#c8a452]">Projects</Link>
            <Link to="/request" className="transition-colors hover:text-[#c8a452]">Request a Survey</Link>
            <Link to="/contact" className="transition-colors hover:text-[#c8a452]">Contact</Link>
          </div>
        </div>
      </footer>

      {/* CONFIRMATION */}
      <Dialog open={!!confirmation} onOpenChange={(o) => !o && setConfirmation(null)}>
        <DialogContent data-testid="contact-confirmation" className="max-w-md text-center">
          <DialogTitle className="sr-only">Message Received</DialogTitle>
          <div className="flex flex-col items-center py-4">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#ecfdf5] text-[#047857]">
              <CheckCircle2 className="h-8 w-8" />
            </span>
            <h3 className="mt-4 font-serif text-2xl font-bold text-[#163323]">Message Received</h3>
            <p className="mt-2 text-sm text-[#5a6660]">
              Thank you for reaching out. A representative will respond as soon as possible.
            </p>
            <div className="mt-4 w-full rounded-lg border border-[#e5d5ab] bg-[#f6f1e3] p-4">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#8e6e23]">Inquiry Reference</span>
              <div data-testid="confirmation-ref" className="mt-1 font-mono text-2xl font-bold text-[#163323]">
                {confirmation?.reference_number}
              </div>
            </div>
            <p className="mt-3 text-xs text-[#9aa5a0]">A confirmation email has been sent to your inbox.</p>
            <Button data-testid="confirmation-close" onClick={() => setConfirmation(null)}
              className="mt-5 w-full rounded-md bg-[#163323] font-semibold text-white hover:bg-[#0d2116]">
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
